<template>
    <bs5-base-modal title="Generate License" :visible="visible" @close="closeModal">
        <template v-slot:default>
            <form @submit.prevent="generateLicense">
                <div class="mb-3">
                    <bs5-base-label for="licenceRequest">Licence Request File</bs5-base-label>
                    <input id="licenceRequest" type="file" class="form-control" @change="handleFileUpload" />
                </div>

                <div class="mb-3">
                    <bs5-base-label for="validTo">Valid To</bs5-base-label>
                    <bs5-base-input id="validTo" type="date" v-model="license.validTo" required />
                </div>

                <div class="mb-3">
                    <bs5-base-label>Features</bs5-base-label>
                    <div>
                        <label v-for="(label, key) in availableFeatures" :key="key" class="form-check">
                            <input type="checkbox" class="form-check-input" v-model="license[key]" />
                            {{ label }}
                        </label>
                    </div>
                </div>
            </form>
        </template>

        <template v-slot:footer>
            <bs5-cancel-button @click="closeModal" />
            <bs5-save-button :loading="saving" @click="generateLicense">Generate</bs5-save-button>
        </template>
    </bs5-base-modal>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import licenseService from "@/services/license.service";
import { LicenseRequest } from "@/models/licence-request.model";
import { showToast } from "@/utils/toast";

export default defineComponent({
    name: "GenerateLicenseModal",
    props: {
        visible: { type: Boolean, required: true },
        clientId: { type: String, required: true },
    },
    emits: ["close", "license-generated"],
    data() {
        return {
            license: ref<Omit<LicenseRequest, "licenceRequest">>({
                validTo: "",
                hasDiscussion: false,
                hasAV: false,
                hasFA: false,
                hasVoting: false,
                hasNfc: false,
                hasOnlineStatus: false,
                hasWirelessData: false,
                hasScreenIdentification: false,
            }),
            licenceRequest: ref<File | null>(null),
            saving: ref(false),
            availableFeatures: {
                hasDiscussion: "Discussion",
                hasAV: "Ad vocem",
                hasFA: "Formal application",
                hasVoting: "Voting",
                hasNfc: "NFC",
                hasOnlineStatus: "Online Status",
                hasWirelessData: "Wireless Data",
                hasScreenIdentification: "Screen Identification",
            },
        };
    },
    methods: {
        handleFileUpload(event: Event) {
            const target = event.target as HTMLInputElement;
            if (target.files?.length) {
                this.licenceRequest = target.files[0];
            }
        },
        async generateLicense() {
            this.saving = true;
            try {
                await licenseService.generateLicense(this.clientId, {
                    ...this.license,
                    licenceRequest: this.licenceRequest,
                });
                this.$emit("license-generated");
                this.closeModal();
                showToast("License generated!", "success");
            } catch (error) {
                showToast("Failed to generate license!", "error");
                console.log(error);
            } finally {
                this.saving = false;
            }
        },
        closeModal() {
            this.$emit("close");
        },
    },
});
</script>